/*
  Rostund – global logik. Byggd från prototypens app.js.
  Varukorgen använder Shopifys Cart AJAX API (/cart/add.js, /cart/change.js) och
  hämtar om varukorgspanelen via Section Rendering API (sections=cart-drawer).
  Konfiguration (vägar, valutaformat, texter, rabattsteg) sätts i layout/theme.liquid.
*/
(() => {
  const T = (window.theme = window.theme || {});
  T.strings = T.strings || {};
  T.routes = T.routes || {};
  const $ = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => [...el.querySelectorAll(sel)];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
  const fill = (s, vars) => String(s || "").replace(/\[(\w+)\]/g, (_, k) => (k in vars ? vars[k] : ""));

  /* ---- Pengar enligt butikens valutaformat (Inställningar > Butiksinformation) ---- */
  function formatMoney(cents, format = T.moneyFormat || "{{amount_no_decimals}} kr") {
    const value = Math.round(Number(cents) || 0) / 100;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : "amount";
    const decimals = key.includes("no_decimals") ? 0 : 2;
    let thousands = ",";
    let point = ".";
    if (key.includes("period_and_space")) {
      thousands = " ";
      point = ".";
    } else if (key.includes("space_separator")) {
      thousands = " ";
      point = ",";
    } else if (key.includes("comma_separator")) {
      thousands = ".";
      point = ",";
    } else if (key.includes("apostrophe_separator")) {
      thousands = "'";
      point = ".";
    }
    const [whole, fraction] = value.toFixed(decimals).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return format.replace(/\{\{.*?\}\}/, decimals ? `${grouped}${point}${fraction}` : grouped);
  }

  /* Rabattsteg: [[2,10],[3,15],[4,20]] -> procent för n produkter */
  const tierPct = (n) => (T.tiers || []).filter((t) => n >= t[0]).map((t) => t[1]).pop() || 0;
  const nextTier = (n) => (T.tiers || []).find((t) => t[0] > n && t[1] > 0) || null;

  /* ---- Toast ---- */
  let toastTimer;
  function toast(msg) {
    const el = $(".toast");
    if (!el) return;
    $("span", el).textContent = msg;
    el.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("is-on"), 2400);
  }

  /* ---- Varukorgspanel ---- */
  let lastFocus = null;
  function openCart() {
    lastFocus = document.activeElement;
    document.body.classList.add("cart-open");
    document.body.classList.remove("menu-open");
    $("#cart-drawer")?.setAttribute("aria-hidden", "false");
    document.documentElement.style.overflow = "hidden";
    setTimeout(() => $("#cart-drawer [data-close-cart]")?.focus(), 50);
  }
  function closeCart() {
    if (!document.body.classList.contains("cart-open")) return;
    document.body.classList.remove("cart-open");
    $("#cart-drawer")?.setAttribute("aria-hidden", "true");
    document.documentElement.style.overflow = "";
    lastFocus?.focus?.();
  }

  function updateCount(count) {
    $$(".cart-count").forEach((b) => {
      b.textContent = count;
      b.classList.toggle("has-items", count > 0);
    });
  }

  /* Sektioner som ska renderas om efter varje ändring: panelen och ev. varukorgssidan */
  function sectionIds() {
    const ids = ["cart-drawer"];
    const page = $("[data-cart-section]");
    if (page) ids.push(page.dataset.cartSection);
    return ids.join(",");
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ...body, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || T.strings.error);
    return data;
  }

  function renderSections(sections) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, "text/html");
    if (sections["cart-drawer"]) {
      const fresh = parse(sections["cart-drawer"]).querySelector("#cart-drawer");
      const drawer = $("#cart-drawer");
      if (fresh && drawer) {
        drawer.innerHTML = fresh.innerHTML;
        drawer.dataset.count = fresh.dataset.count;
        updateCount(+fresh.dataset.count || 0);
      }
    }
    const page = $("[data-cart-section]");
    const pageHtml = page && sections[page.dataset.cartSection];
    if (pageHtml) {
      const fresh = parse(pageHtml).querySelector("[data-cart-section]");
      if (fresh) page.innerHTML = fresh.innerHTML;
    }
  }

  /* items: [{ id: variantId, quantity }]. Returnerar true om det gick bra. */
  async function addToCart(items, name, trigger) {
    trigger?.classList.add("is-busy");
    try {
      const data = await cartRequest(T.routes.cartAdd || "/cart/add.js", { items });
      renderSections(data.sections);
      afterAdd(name);
      return true;
    } catch (err) {
      toast(err.message || T.strings.error);
      return false;
    } finally {
      trigger?.classList.remove("is-busy");
    }
  }

  async function changeLine(key, quantity, trigger) {
    const line = trigger?.closest(".line");
    line?.classList.add("is-busy");
    try {
      const data = await cartRequest(T.routes.cartChange || "/cart/change.js", { id: key, quantity });
      renderSections(data.sections);
    } catch (err) {
      toast(err.message || T.strings.error);
      line?.classList.remove("is-busy");
    }
  }

  function afterAdd(name) {
    const badge = $(".cart-count");
    if (badge && !reduceMotion) {
      badge.classList.remove("bump");
      void badge.offsetWidth;
      badge.classList.add("bump");
    }
    toast(fill(T.strings.added, { name }));
    setTimeout(openCart, reduceMotion ? 0 : 350);
  }

  /* ---- Globala klick ---- */
  function bindGlobal() {
    document.addEventListener("click", (e) => {
      const t = e.target.closest("button, a, [data-close-cart]");
      if (!t) return;

      if (t.matches("[data-open-cart]")) {
        e.preventDefault();
        return openCart();
      }
      if (t.matches("[data-close-cart]")) {
        closeCart();
        if (t.tagName !== "A") return;
      }
      if (t.matches(".menu-toggle")) {
        const open = document.body.classList.toggle("menu-open");
        t.setAttribute("aria-expanded", String(open));
        t.setAttribute("aria-label", open ? T.strings.menuClose : T.strings.menuOpen);
        return;
      }
      if (t.closest(".mobile-menu") && t.tagName === "A") document.body.classList.remove("menu-open");

      if (t.matches("[data-qty-change]")) {
        return changeLine(t.dataset.key, Math.max(0, +t.dataset.quantity || 0), t);
      }
      if (t.matches("[data-add-variant]")) {
        e.preventDefault();
        return addToCart([{ id: Number(t.dataset.addVariant), quantity: 1 }], t.dataset.title || "", t);
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      closeCart();
      if (document.body.classList.contains("menu-open")) {
        document.body.classList.remove("menu-open");
        $(".menu-toggle")?.setAttribute("aria-expanded", "false");
      }
    });

    // Header får en hårlinje när man scrollat förbi toppen
    const sentinel = $("#stuck-sentinel");
    const header = $(".site-header");
    if (sentinel && header) {
      new IntersectionObserver(([entry]) => header.classList.toggle("is-stuck", !entry.isIntersecting)).observe(sentinel);
    }
  }

  /* ---- Inanimering vid scroll ---- */
  let revealObserver = null;
  function bindReveal(root = document) {
    const els = $$("[data-reveal]:not(.is-in):not([data-reveal-bound])", root);
    if (!("IntersectionObserver" in window) || reduceMotion) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    revealObserver ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("is-in");
            revealObserver.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => {
      el.setAttribute("data-reveal-bound", "");
      revealObserver.observe(el);
    });
  }

  /* ---- Nyhetsbrev: validera innan Shopifys kundformulär skickas ---- */
  function bindNewsletter() {
    $$("#nl-form, .nl-form").forEach((form) => {
      form.addEventListener("submit", (e) => {
        const input = $('input[type="email"]', form);
        const help = $(".nl-help", form);
        if (!input) return;
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(input.value.trim());
        if (ok) {
          input.removeAttribute("aria-invalid");
          return;
        }
        e.preventDefault();
        if (help) {
          help.classList.add("is-error");
          help.textContent = T.strings.newsletterInvalid;
        }
        input.setAttribute("aria-invalid", "true");
        input.focus();
      });
    });
  }

  /* ---- Temaredigeraren: öppna panelen när den markeras, animera in nya sektioner ---- */
  document.addEventListener("shopify:section:load", (e) => bindReveal(e.target));
  document.addEventListener("shopify:section:select", (e) => {
    if (e.target.querySelector("#cart-drawer")) openCart();
  });
  document.addEventListener("shopify:section:deselect", (e) => {
    if (e.target.querySelector("#cart-drawer")) closeCart();
  });

  Object.assign(T, { $, $$, esc, fill, formatMoney, tierPct, nextTier, toast, openCart, closeCart, addToCart, changeLine, reduceMotion });

  bindGlobal();
  bindReveal();
  bindNewsletter();

  /* ---- Videor som spelas av sig själva: stoppa dem för den som valt mindre rörelse ---- */
  if (reduceMotion) {
    $$("video[autoplay]").forEach((v) => {
      v.removeAttribute("autoplay");
      v.pause();
      // i produktgalleriet kan man starta den själv, på startsidan visas bara stillbilden
      if (v.closest(".main-img")) v.controls = true;
    });
  }
})();
